import React, { useState, useEffect } from "react";
import {
	Modal,
	Form,
	Select,
	DatePicker,
	Button,
	Table,
	Tag,
	message,
	Row,
	Col,
	Typography,
	Space,
	Card,
	Input,
	Tabs,
} from "antd";
import {
	TeamOutlined,
	PlusOutlined,
	DeleteOutlined,
	EditOutlined,
} from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

const EditServingDepartmentModal = ({
	visible,
	onCancel,
	onSuccess,
	userId,
	userName,
}) => {
	const isMobile = useIsMobile();
	const [loading, setLoading] = useState(false);
	const [departments, setDepartments] = useState([]);
	const [servingDepartments, setServingDepartments] = useState([]);
	const [changeTypes, setChangeTypes] = useState([]);
	const [addForm] = Form.useForm();
	const [editForm] = Form.useForm();
	const [removeForm] = Form.useForm();
	const [selectedServingDept, setSelectedServingDept] = useState(null);
	const [selectedDepartmentsForDelete, setSelectedDepartmentsForDelete] =
		useState([]);

	useEffect(() => {
		if (visible) {
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run only when visible
	}, [visible]);

	const fetchData = async () => {
		try {
			setLoading(true);

			// Fetch all departments
			const departmentsResponse = await useAxios.get(
				"/department/departments/"
			);
			if (departmentsResponse.status === 200) {
				setDepartments(departmentsResponse.data);
			}

			// Fetch serving departments for the user
			const servingResponse = await useAxios.get(
				`/users/serving-departments/${userId}/`
			);
			if (servingResponse.status === 200) {
				setServingDepartments(servingResponse.data);
			}

			// Fetch change types
			const changeTypesResponse = await useAxios.get(
				"/users/serving-department-change-types/"
			);
			if (changeTypesResponse.status === 200) {
				setChangeTypes(changeTypesResponse.data);
			}
		} catch (error) {
			message.error("데이터를 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleAddServingDepartment = async () => {
		try {
			const values = await addForm.validateFields();

			const response = await useAxios.post(
				`/users/serving-departments/${userId}/`,
				{
					department_id: values.department_id,
					change_record: {
						changed_date:
							values.changed_date.format(
								"YYYY-MM-DD"
							),
						reason: values.reason || "",
					},
				}
			);

			if (response.status === 201) {
				message.success(
					"봉사기관이 성공적으로 추가되었습니다."
				);
				addForm.resetFields();
				fetchData();
			}
		} catch (error) {
			if (error.response && error.response.status === 409) {
				message.error("이미 존재하는 봉사기관입니다.");
			} else {
				message.error("봉사기관 추가에 실패했습니다.");
			}
		}
	};

	const handleEditChangeStatus = async () => {
		try {
			const values = await editForm.validateFields();

			// Update existing serving department status using PUT
			const response = await useAxios.put(
				`/users/serving-departments/${userId}/`,
				{
					department_id:
						selectedServingDept.department_id,
					change_record: {
						changed_date:
							values.changed_date.format(
								"YYYY-MM-DD"
							),
						change_type: values.change_type,
						reason: values.reason || "",
					},
				}
			);

			if (response.status === 200) {
				message.success(
					"봉사기관 상태가 성공적으로 변경되었습니다."
				);
				editForm.resetFields();
				setSelectedServingDept(null);
				fetchData();
			}
		} catch (error) {
			message.error("봉사기관 상태 변경에 실패했습니다.");
		}
	};

	const handleRemoveServingDepartments = async () => {
		if (selectedDepartmentsForDelete.length === 0) {
			message.warning("삭제할 봉사기관을 선택해주세요.");
			return;
		}

		try {
			const values = await removeForm.validateFields();

			// Delete each selected department
			for (const servingDeptId of selectedDepartmentsForDelete) {
				const response = await useAxios.delete(
					`/users/serving-departments/${userId}/`,
					{
						data: {
							serving_dept_id:
								servingDeptId,
							change_record: {
								changed_date:
									values.changed_date.format(
										"YYYY-MM-DD"
									),
								change_type:
									values.change_type,
								reason:
									values.reason ||
									"",
							},
						},
					}
				);

				if (response.status !== 204) {
					throw new Error(
						`Failed to delete department ${servingDeptId}`
					);
				}
			}

			message.success(
				`${selectedDepartmentsForDelete.length}개의 봉사기관이 성공적으로 삭제되었습니다.`
			);
			removeForm.resetFields();
			setSelectedDepartmentsForDelete([]);
			fetchData();
		} catch (error) {
			message.error("봉사기관 삭제에 실패했습니다.");
		}
	};

	const handleEditClick = (record) => {
		setSelectedServingDept(record);
		editForm.setFieldsValue({
			department_name: record.department_name,
			change_type: "휴직", // Default to 휴직
		});
	};

	const statusChangeTypes = changeTypes.filter(
		(type) => type.name === "휴직" || type.name === "복직"
	);

	const removeChangeTypes = changeTypes.filter(
		(type) => type.name === "퇴직"
	);

	return (
		<Modal
			title={
				<Space>
					<TeamOutlined />
					봉사기관 관리
				</Space>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			width={isMobile ? "100%" : 720}>
			{loading ? (
				<div
					style={{
						textAlign: "center",
						padding: "20px",
					}}>
					<Text>데이터를 불러오는 중...</Text>
				</div>
			) : (
				<Tabs defaultActiveKey="1">
					{/* Add Department Tab */}
					<TabPane tab="봉사기관 추가" key="1">
						<Card size="small">
							<Form
								form={addForm}
								layout="vertical"
								onFinish={
									handleAddServingDepartment
								}>
								<Row
									gutter={
										16
									}>
									<Col
										span={
											12
										}>
										<Form.Item
											name="department_id"
											label="부서"
											rules={[
												{
													required: true,
													message: "부서를 선택해주세요",
												},
											]}>
											<Select placeholder="부서 선택">
												{departments.map(
													(
														dept
													) => (
														<Option
															key={
																dept.id
															}
															value={
																dept.id
															}>
															{
																dept.name
															}
														</Option>
													)
												)}
											</Select>
										</Form.Item>
									</Col>
									<Col
										span={
											12
										}>
										<Form.Item
											name="changed_date"
											label="변경일"
											rules={[
												{
													required: true,
													message: "변경일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												placeholder="변경일 선택"
												style={{
													width: "100%",
												}}
												format="YYYY-MM-DD"
											/>
										</Form.Item>
									</Col>
								</Row>
								<Row>
									<Col
										span={
											24
										}>
										<Form.Item
											name="reason"
											label="사유">
											<Input.TextArea
												placeholder="사유 입력"
												rows={
													2
												}
											/>
										</Form.Item>
									</Col>
								</Row>
								<Form.Item>
									<Button
										type="primary"
										icon={
											<PlusOutlined />
										}
										htmlType="submit">
										봉사기관
										추가
									</Button>
								</Form.Item>
							</Form>
						</Card>
					</TabPane>

					{/* Edit Change Status Tab */}
					<TabPane tab="상태 변경" key="2">
						<Row gutter={16}>
							<Col span={12}>
								<Card
									title="현재 봉사기관"
									size="small"
									style={{
										marginBottom: 16,
									}}>
									<Table
										dataSource={
											servingDepartments
										}
										columns={[
											{
												title: "부서명",
												dataIndex: "department_name",
												key: "department_name",
											},
											{
												title: "상태",
												dataIndex: "status",
												key: "status",
											},
											{
												title: "작업",
												key: "action",
												render: (
													_,
													record
												) => (
													<Button
														type="text"
														icon={
															<EditOutlined />
														}
														size="small"
														onClick={() =>
															handleEditClick(
																record
															)
														}>
														상태변경
													</Button>
												),
											},
										]}
										rowKey="id"
										pagination={
											false
										}
										size="small"
									/>
								</Card>
							</Col>
							<Col span={12}>
								<Card
									title="상태 변경"
									size="small">
									{selectedServingDept ? (
										<Form
											form={
												editForm
											}
											layout="vertical"
											onFinish={
												handleEditChangeStatus
											}>
											<Form.Item label="부서명">
												<Input
													value={
														selectedServingDept.department_name
													}
													disabled
												/>
											</Form.Item>
											<Row
												gutter={
													16
												}>
												<Col
													span={
														12
													}>
													<Form.Item
														name="changed_date"
														label="변경일"
														rules={[
															{
																required: true,
																message: "변경일을 선택해주세요",
															},
														]}>
														<DatePicker
															inputReadOnly={isMobile}
															placeholder="변경일 선택"
															style={{
																width: "100%",
															}}
															format="YYYY-MM-DD"
														/>
													</Form.Item>
												</Col>
												<Col
													span={
														12
													}>
													<Form.Item
														name="change_type"
														label="변경유형"
														rules={[
															{
																required: true,
																message: "변경유형을 선택해주세요",
															},
														]}>
														<Select placeholder="변경유형 선택">
															{statusChangeTypes.map(
																(
																	type
																) => (
																	<Option
																		key={
																			type.id
																		}
																		value={
																			type.name
																		}>
																		{
																			type.name
																		}
																	</Option>
																)
															)}
														</Select>
													</Form.Item>
												</Col>
											</Row>
											<Form.Item
												name="reason"
												label="사유">
												<Input.TextArea
													placeholder="사유 입력"
													rows={
														2
													}
												/>
											</Form.Item>
											<Form.Item>
												<Button
													type="primary"
													htmlType="submit">
													상태
													변경
												</Button>
											</Form.Item>
										</Form>
									) : (
										<div
											style={{
												textAlign: "center",
												padding: "20px",
											}}>
											<Text type="secondary">
												상태를
												변경할
												봉사기관을
												선택해주세요.
											</Text>
										</div>
									)}
								</Card>
							</Col>
						</Row>
					</TabPane>

					{/* Remove Department Tab */}
					<TabPane tab="봉사기관 삭제" key="3">
						<Row gutter={16}>
							<Col span={12}>
								<Card
									title="삭제할 봉사기관 선택"
									size="small"
									style={{
										marginBottom: 16,
									}}
									extra={
										<Button
											type="primary"
											danger
											icon={
												<DeleteOutlined />
											}
											onClick={
												handleRemoveServingDepartments
											}
											disabled={
												selectedDepartmentsForDelete.length ===
												0
											}>
											선택한
											봉사기관
											삭제
										</Button>
									}>
									<Table
										dataSource={
											servingDepartments
										}
										columns={[
											{
												title: "선택",
												key: "selection",
												width: 60,
												render: (
													_,
													record
												) => (
													<input
														type="checkbox"
														checked={selectedDepartmentsForDelete.includes(
															record.id
														)}
														onChange={(
															e
														) => {
															if (
																e
																	.target
																	.checked
															) {
																setSelectedDepartmentsForDelete(
																	[
																		...selectedDepartmentsForDelete,
																		record.id,
																	]
																);
															} else {
																setSelectedDepartmentsForDelete(
																	selectedDepartmentsForDelete.filter(
																		(
																			id
																		) =>
																			id !==
																			record.id
																	)
																);
															}
														}}
													/>
												),
											},
											{
												title: "부서명",
												dataIndex: "department_name",
												key: "department_name",
											},
											{
												title: "상태",
												dataIndex: "status",
												key: "status",
												render: (
													status
												) => (
													<Tag
														color={
															status ===
															"활동"
																? "green"
																: status ===
																  "휴직"
																? "orange"
																: status ===
																  "복직"
																? "blue"
																: "default"
														}
														size="small">
														{
															status
														}
													</Tag>
												),
											},
										]}
										rowKey="id"
										pagination={
											false
										}
										size="small"
									/>
								</Card>
							</Col>
							<Col span={12}>
								<Card
									title="삭제 시 기록"
									size="small">
									<Form
										form={
											removeForm
										}
										layout="vertical">
										<Row
											gutter={
												16
											}>
											<Col
												span={
													12
												}>
												<Form.Item
													name="changed_date"
													label="변경일"
													rules={[
														{
															required: true,
															message: "변경일을 선택해주세요",
														},
													]}>
													<DatePicker
														inputReadOnly={isMobile}
														placeholder="변경일 선택"
														style={{
															width: "100%",
														}}
														format="YYYY-MM-DD"
													/>
												</Form.Item>
											</Col>
											<Col
												span={
													12
												}>
												<Form.Item
													name="change_type"
													label="변경유형"
													rules={[
														{
															required: true,
															message: "변경유형을 선택해주세요",
														},
													]}>
													<Select placeholder="변경유형 선택">
														{removeChangeTypes.map(
															(
																type
															) => (
																<Option
																	key={
																		type.id
																	}
																	value={
																		type.name
																	}>
																	{
																		type.name
																	}
																</Option>
															)
														)}
													</Select>
												</Form.Item>
											</Col>
										</Row>
										<Form.Item
											name="reason"
											label="사유">
											<Input.TextArea
												placeholder="사유 입력"
												rows={
													2
												}
											/>
										</Form.Item>
									</Form>
								</Card>
							</Col>
						</Row>
					</TabPane>
				</Tabs>
			)}
		</Modal>
	);
};

export default EditServingDepartmentModal;
